import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IError, IGroups } from '../../../../../interfaces';
import { CrmService } from '../../../../../services/crm.service';
import { BaseService } from '../../../../../services/base.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { ERROR_MESSAGES, activeTranslations, PERMISSIONS } from '../../../../../enum/constants';
import { UpdateManagerForm } from './update-manager.interface';
import * as _ from 'lodash';
import { DialogService } from '../../../../../services/dialog.service';

@Component({
  selector: 'app-update-manager',
  templateUrl: './update-manager.component.html',
  styleUrl: './update-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UpdateManagerComponent implements OnInit, OnDestroy {
  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  // Destroy
  private readonly destroy$ = new Subject<null>();

  // Form
  public updateManagerForm!: FormGroup<UpdateManagerForm>;

  // Var`s
  public groups: IGroups[] = [];

  public manager: any;
  public manager_id!: string;
  public isHasChange: boolean = false;
  public canManageTeam: boolean = false;
  public canChangeOwnRole: boolean = false;

  public managers: string[] = [];

  constructor(
    public crmService: CrmService,
    public baseService: BaseService,
    public dialogService: DialogService,
    public notificationService: NotificationService,
  ) {}

  public ngOnInit(): void {
    this.manager_id = this.payload?.data?.manager_id;
    this.managers = this.payload?.data?.managers;
    this.canManageTeam = this.baseService.isHasPermission(PERMISSIONS.TEAM_ACTION);
    this.canChangeOwnRole = this.baseService.isHasPermission(PERMISSIONS.ROLE_CHANGE_OWN_ROLE_ACCESS);

    this.onGetAllGroups();
    if (this.manager_id) this.onGetManagerById();
    else this.initForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public onCloseSidebar(): void {
    this.close.emit('close');
  }

  private initForm(): void {
    if (this.manager_id) {
      this.updateManagerForm = new FormGroup({
        group_id: new FormControl(this.manager?.group_id, Validators.required),
      });
      this.onCreateGroupFormValueChange();
    } else {
      this.updateManagerForm = new FormGroup({
        group_id: new FormControl('', Validators.required),
      });
    }
  }

  private onCreateGroupFormValueChange() {
    const initialValue: any = { ...this.updateManagerForm.getRawValue() };
    this.updateManagerForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.isHasChange = !_.isEqual(value, initialValue);
    });
  }

  public onUpdateManager(): void {
    if (this.updateManagerForm.valid) {
      const { group_id } = this.updateManagerForm.getRawValue();
      const role = this.groups.find((v) => v.id === group_id);

      if (this.manager?.id) {
        const isCurrentUser = this.manager.id === this.baseService.currentUserInfo?.id;
        if (!this.canManageTeam && !(this.canChangeOwnRole && isCurrentUser)) return;

        this.crmService.updateManager({ role: role?.name, group_id: role?.id }, this.manager?.id).subscribe({
          next: () => {
            this.close.emit('success');
          },
          error: (e: any) => {
            (e.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code]);
            });
          },
        });
      } else if (this.managers?.length) {
        if (!this.canManageTeam) return;

        this.crmService
          .bulkUpdateManagers({ employees: this.managers, role: role?.name, group_id: role?.id })
          .subscribe({
            next: () => {
              this.close.emit('success');
            },
            error: (e: any) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code]);
              });
            },
          });
      }
    }
  }

  private onGetAllGroups(): void {
    this.crmService
      .getAllGroups({ page: 1, take: 500, access_change_own_role: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (v) => {
          this.groups = v.items;
        },
      });
  }

  private onGetManagerById(): void {
    this.crmService
      .getManagerById(this.manager_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (v) => {
          this.manager = v;
          this.initForm();
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }

  public onDeleteManager() {
    if (!this.canManageTeam || !this.manager?.id) return;

    if (this.manager?.role === 'OWNER') {
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.cannot_remove_owner,
      );
      return;
    }
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.update-manager',
    });

    dialogRef.subscribe({
      next: (res: any) => {
        if (res) {
          this.crmService.deleteManager(this.manager.id).subscribe({
            next: () => {
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success
                  .user_has_been_successfully_removed_from_the_team,
              );
              this.close.emit('success');
            },
            error: (e: any) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code]);
              });
            },
          });
        }
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code]);
        });
      },
    });
  }
}
